import { Box, IconButton, lighten, Stack, Typography } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import { type FC } from "react";
import type { SystemAnnouncement } from "../api/userInfo";
import { InfoIcon, XIcon } from "./icons";
import { useLocalStorage } from "./useLocalStorage";

type SystemAnnouncementBannerProps = {
  systemAnnouncement?: SystemAnnouncement;
};

const STORAGE_KEY = `privacy:system-announcement:collapsed`;

export const SystemAnnouncementBanner: FC<SystemAnnouncementBannerProps> = ({ systemAnnouncement }) => {
  const { t } = useTranslation();
  const [collapsedSystemAnnouncementId, setCollapsedSystemAnnouncementId] = useLocalStorage<string | null>(
    STORAGE_KEY,
    Boolean,
    null
  );
  const isCollapsed = systemAnnouncement?.id === collapsedSystemAnnouncementId;

  if (!systemAnnouncement) return null;

  return (
    <Stack
      role="region"
      aria-label={systemAnnouncement.title}
      direction="row"
      sx={[
        {
          justifyContent: "space-between",
        },
        theme => ({
          position: "fixed",
          bottom: theme.spacing(2),
          right: theme.spacing(3),
          boxShadow: "0 2px 4px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.06)",
          zIndex: 10000,
          backgroundColor: lighten(theme.palette.info["50"], 0.4),
          color: theme.palette.info.dark,
          ...(isCollapsed
            ? {
                width: "45px",
                height: "45px",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }
            : { width: "min(500px, 100%)", borderRadius: 1, px: 3, py: 2.5 }),
        }),
      ]}
    >
      {isCollapsed ? (
        <IconButton
          sx={{ p: 0, borderRadius: "50%", width: "100%", height: "100%" }}
          aria-label={t("SystemAnnouncementBanner.ExpandButton")}
          onClick={() => setCollapsedSystemAnnouncementId(null)}
        >
          <InfoIcon size={20} color={theme => theme.palette.info[700]} />
        </IconButton>
      ) : (
        <Box>
          <Stack direction="row" sx={{ gap: 2 }}>
            <InfoIcon size={18} color={theme => theme.palette.info[700]} />
            <Stack sx={{ mr: 5, gap: 0.2 }}>
              <Typography
                variant="h3"
                sx={theme => ({
                  fontSize: theme.typography.pxToRem(14),
                  fontWeight: 600,
                  color: theme.palette.info.dark,
                })}
              >
                {systemAnnouncement.title}
              </Typography>

              <Typography
                sx={theme => ({
                  fontSize: theme.typography.pxToRem(14),
                  color: theme.palette.info[700],
                })}
              >
                {systemAnnouncement.message}
              </Typography>
            </Stack>
          </Stack>

          <IconButton
            sx={theme => ({
              color: theme.palette.info[700],
              position: "absolute",
              right: theme.spacing(1),
              top: theme.spacing(1),
            })}
            aria-label={t("SystemAnnouncementBanner.CollapseButton")}
            onClick={() => setCollapsedSystemAnnouncementId(systemAnnouncement.id)}
          >
            <XIcon size={14} />
          </IconButton>
        </Box>
      )}
    </Stack>
  );
};
